import {
  EsvcsProductOfferings,
  EsvcsProductOfferings_Descriptions,
} from "csd.phoenix.models/EsvcsProductOfferings";
import {
  EsvcsEnrollmentStatuses,
  EsvcsEnrollmentStatuses_Descriptions,
} from "csd.phoenix.models/EsvcsEnrollmentStatuses";
import {
  DpmsTypes,
  DpmsTypes_Descriptions,
} from "csd.phoenix.models/DpmsTypes";
import { IFieldOption } from "./form/models";
import { getEnumValues } from "../../utils/objectUtils";
import { isLocaleUK } from "../../i18n";
import { isPartnerProductOffering } from "../../stores/enrollmentsStore";
import { TenantLocales } from 'csd.phoenix.models/TenantLocales';

export function getProductList(
  isPartnerMode: boolean,
  isNewEnrollment: boolean
) {
  const productFilter = isPartnerMode
    ? (p: EsvcsProductOfferings) =>
        isPartnerProductOffering(p)
    : (p: EsvcsProductOfferings) =>
        !isNewEnrollment || !isPartnerProductOffering(p);

  const ProductList: IFieldOption[] = getEnumValues(EsvcsProductOfferings)
    .filter(productFilter)
    .map((p) => ({ value: p, text: EsvcsProductOfferings_Descriptions[p] }))
    .sort((a, b) => a.text.localeCompare(b.text));

  return ProductList;
}

export function getEnrollmentStatusList() {
  const statusList: IFieldOption[] = getEnumValues(EsvcsEnrollmentStatuses).map(
    (s) => ({ value: s, text: EsvcsEnrollmentStatuses_Descriptions[s] })
  );

  return statusList;
}

export function getDpmsList(productOffering?: EsvcsProductOfferings): IFieldOption[] {
  // Helper to avoid repeating { value, text } boilerplate for each entry.
  const opt = (value: DpmsTypes): IFieldOption => ({ value, text: DpmsTypes_Descriptions[value] });

  // OMS 9 is supported by SCA and AutomatedConversion, and is included in the unfiltered list.
  const includeOms9 = !productOffering
    || productOffering === EsvcsProductOfferings.AutomatedConversion
    || productOffering === EsvcsProductOfferings.SenseiCloudApps;

  // DSN and OmsVision are legacy DPMS types only relevant to AutomatedConversion.
  const includeLegacy = !productOffering
    || productOffering === EsvcsProductOfferings.AutomatedConversion;

  // CMP is excluded from SCA (requires a real DPMS) and AutomatedConversion.
  const includeCmp = productOffering !== EsvcsProductOfferings.AutomatedConversion
    && productOffering !== EsvcsProductOfferings.SenseiCloudApps;

  if (isLocaleUK()) {
    // UK only supports R4 (and CMP where applicable).
    return [
      opt(DpmsTypes.R4),
      ...(includeCmp ? [opt(DpmsTypes.CMP)] : []),
    ];
  }

  return [
    opt(DpmsTypes.SoftDent),
    opt(DpmsTypes.Orthotrac),
    ...(includeOms9 ? [opt(DpmsTypes.WinOMS9)] : []),  // inserted before WinOMS (OMS 10)
    opt(DpmsTypes.WinOMS),
    opt(DpmsTypes.PracticeWorks),
    ...(includeLegacy ? [opt(DpmsTypes.DSN)] : []),
    ...(includeLegacy ? [opt(DpmsTypes.OmsVision)] : []),
    ...(includeCmp ? [opt(DpmsTypes.CMP)] : []),
  ];
}

export function getLocalesList() {
  let localeList: IFieldOption[] = [];

  if (isLocaleUK()) {
    localeList = [
      { value: TenantLocales.England, text: "England" },
      { value: TenantLocales.Wales, text: "Wales" },
    ];
  } else {
    localeList = [{ value: TenantLocales.USA, text: "USA" }];
  }

  return localeList;
}